import { useSyncExternalStore } from "react";
import { SESSION_ID } from "./session.js";
import { invalidate } from "./useFetch.js";

/**
 * Live tasks for this page's session, pushed over a WebSocket from the server's
 * collector (full list each time, throttled server-side to 50ms). No polling, no
 * embed machinery — just a store + a hook. The WS is session-scoped, so other
 * sessions' query floods never reach us. (EXPERIMENT — see /server/livebus.ts.)
 */

export interface LiveTask {
  id: string;
  name: string;
  status: "active" | "done" | "error";
  sourcePath: string | null;
  embedId: string | null;
  startMs: number;
  endMs: number | null;
}

let tasks: LiveTask[] = [];
const listeners = new Set<() => void>();
function emit(): void {
  for (const l of listeners) l();
}

let ws: WebSocket | null = null;
let backoff = 250;
let started = false;

function connect(): void {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  ws = new WebSocket(`${proto}://${location.host}/api/events?session=${SESSION_ID}`);
  ws.onopen = () => {
    backoff = 250;
  };
  ws.onmessage = (e) => {
    try {
      const m = JSON.parse(e.data as string);
      if (m && m.type === "tasks") {
        tasks = m.tasks as LiveTask[];
        emit();
      } else if (m && m.type === "git") {
        onGitEvent(m.repo as string | undefined);
      } else if (m && m.type === "change") {
        onChangeEvent(m.repo as string | undefined, (m.paths as string[]) ?? []);
      }
    } catch {
      /* ignore malformed frames */
    }
  };
  ws.onclose = () => {
    ws = null;
    setTimeout(connect, backoff); // WS has no auto-reconnect; back off and retry
    backoff = Math.min(backoff * 2, 5000);
  };
  ws.onerror = () => ws?.close();
}

function ensureConnected(): void {
  if (!started) {
    started = true;
    connect();
  }
}

/** Invalidate one repo's git-derived caches (keys are repo-scoped:
 * `tree:<repo>`, `page:<repo>/…`, `commit:<repo>`, `repo:<repo>`). With no repo
 * (shouldn't happen) fall back to every repo's. */
function invalidateRepo(repo: string | undefined): void {
  if (repo) {
    invalidate(`tree:${repo}`);
    invalidate(`page:${repo}/`);
    invalidate(`commit:${repo}`);
    invalidate(`repo:${repo}`);
  } else {
    invalidate("tree");
    invalidate("page:");
    invalidate("commit");
    invalidate("repo");
  }
}

/**
 * One repo changed under us — someone published, a push landed from outside and
 * the server synced, or this user's branch was rebased/abandoned in another tab.
 * Drop that repo's git-derived caches; mounted consumers (page body, sidebar
 * tree, git card, repo page) refetch immediately. The editor's textarea holds
 * its own local state, so an in-progress edit is never clobbered.
 */
function onGitEvent(repo: string | undefined): void {
  invalidateRepo(repo);
  invalidate("session"); // session (branch, repo list) is instance-global
}

/**
 * Files changed without a commit — on disk beside a cwd-mode server (your
 * editor, a `git pull`, an agent), or in your draft on a deployed one (another
 * tab, or `datawiki sync` pushing from your laptop). Same caches as a `git`
 * event, minus `session`: a file write can't change who you are.
 *
 * `query:` is normally left alone — dropping it would re-run every embed's
 * query on each keystroke-save. The exception is a changed **query file**: an
 * embed's `@ref` now compiles to different SQL, so the cached rows are simply
 * wrong. Editing a `.sql` locally and watching the chart redraw is the point of
 * that case, and it can only fire when a query file actually moved.
 */
function onChangeEvent(repo: string | undefined, paths: string[]): void {
  invalidateRepo(repo);
  if (paths.some((p) => /\.(sql|query)$/i.test(p))) invalidate("query:");
}

/** Clear this session's tasks — locally and on the server (drops persisted errors). */
export function clearTasks(): void {
  tasks = [];
  emit();
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: "clear" }));
  }
}

/** Subscribe a component to this session's live task list. */
export function useLiveTasks(): LiveTask[] {
  return useSyncExternalStore(
    (cb) => {
      ensureConnected();
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => tasks,
  );
}
